import { z } from 'zod';

/**
 * What a test-connection can conclude.
 *
 * `not_wired` is its own outcome rather than a failure: the platform declares
 * providers whose adapter arrives in a later phase (`smtp` today), and an
 * unregistered probe must say so explicitly. A silent success there would tell
 * an operator that credentials work when nothing was ever contacted — the one
 * answer a test-connection must never give.
 */
export const INTEGRATION_PROBE_CODES = ['ok', 'not_wired', 'not_configured', 'failed'] as const;

export const IntegrationProbeCodeSchema = z.enum(INTEGRATION_PROBE_CODES);

export type IntegrationProbeCode = (typeof INTEGRATION_PROBE_CODES)[number];

/**
 * How much vendor detail is worth keeping.
 *
 * A vendor that answers a probe with an HTML error page hands us its whole
 * body as `error.message`; it is stored and rendered, so it is bounded. The cap
 * is applied at the api's one finalize step (which redacts **first** — a secret
 * cut in half is a secret no substring match can find), never in the
 * constructors below, which build the answer verbatim.
 */
export const INTEGRATION_PROBE_DETAIL_MAX = 2000;

/**
 * The three fields every probe answer carries — **unrefined and un-strict**.
 *
 * It exists to be extended: `.strict().refine(...)` produces a `ZodEffects`,
 * which Zod cannot `.extend()`, so the richer stored answer
 * (`IntegrationProbeStatusSchema`) builds from this object and re-applies
 * {@link probeOkMatchesCode}. Parse against `IntegrationProbeResultSchema`, not
 * against this.
 */
export const IntegrationProbeOutcomeSchema = z.object({
  ok: z.boolean(),
  code: IntegrationProbeCodeSchema,
  detail: z.string().max(INTEGRATION_PROBE_DETAIL_MAX).nullable(),
});

/** `ok` is not an independent fact — it is `code === 'ok'`, in one place. */
export function probeOkMatchesCode(result: { ok: boolean; code: IntegrationProbeCode }): boolean {
  return result.ok === (result.code === 'ok');
}

/** The issue every schema reports when {@link probeOkMatchesCode} fails. */
export const PROBE_OK_ISSUE = {
  message: 'Only the "ok" code reports success.',
  path: ['ok'],
} satisfies { message: string; path: PropertyKey[] };

/**
 * The probe result.
 *
 * A **code**, not a sentence: the api has no i18n catalogs and never renders
 * user-facing copy (U8) — the admin translates the code, exactly as it does for
 * every error code. `detail` is the raw vendor message, shown verbatim to an
 * operator who needs the real reason ("InvalidAccessKeyId" beats "failed").
 */
export const IntegrationProbeResultSchema = IntegrationProbeOutcomeSchema.strict().refine(
  probeOkMatchesCode,
  PROBE_OK_ISSUE,
);

export type IntegrationProbeResult = z.infer<typeof IntegrationProbeResultSchema>;

/** A probe that reached the provider and was accepted. */
export function probeSuccess(detail: string | null = null): IntegrationProbeResult {
  return { ok: true, code: 'ok', detail };
}

/** Any other outcome — always with the code that says which. */
export function probeFailure(
  code: Exclude<IntegrationProbeCode, 'ok'>,
  detail: string | null = null,
): IntegrationProbeResult {
  return { ok: false, code, detail };
}
